import { extent } from 'd3-array';
import { interpolateLab } from 'd3-interpolate';
import { scaleLinear } from 'd3-scale';

import { getRelativeLuminance } from '@revelio/replots';

import { NeutralPalette } from './color-constants';
import type { ColorScheme, ColorSchemeDirection } from './metric-scales';

// https://www.learnui.design/tools/data-color-picker.html
export const NegativeSequentialPalette = [
  '#F2D9DE',
  '#E8B9C4',
  '#D47E93',
  '#C96A83',
  '#B34B6C',
] as const;

export const PositiveSequentialPalette = [
  '#D8F3E0',
  '#B5E5C7',
  '#70CA97',
  '#51B680',
  '#2E9B64',
] as const;

export const NeutralColor = '#F5F5F5';
export const PositiveMaxColor = '#2E9B64';
export const NegativeMaxColor = '#B34B6C';

export const HighlightColor = '#2d426a';

export const DataBorderColor = '#ffffff';
export const FadedBorderColor = '#ffffff';
export const BorderColor = '#8ed1f1';

export const NoDataFillColor = 'transparent';

export const NoDataMajorFillColor = '#d4f0fc';
export const NoDataMinorBorderColor = DataBorderColor;

export function getColorScale(
  values: number[],
  type: ColorScheme,
  direction: ColorSchemeDirection,
  domain?: { min?: number; max?: number; center?: number }
): (value: number) => string {
  if (!values.length) {
    return () => NoDataFillColor;
  }

  const [dataMin, dataMax] = extent(values) as [number, number];

  if (type === 'diverging') {
    const actualCenter = domain?.center ?? 0;
    let minVal = domain?.min ?? dataMin;
    let maxVal = domain?.max ?? dataMax;

    // Ensure symmetry around center for diverging scales
    const maxDeviation = Math.max(
      Math.abs(minVal - actualCenter),
      Math.abs(maxVal - actualCenter)
    );
    minVal = actualCenter - maxDeviation;
    maxVal = actualCenter + maxDeviation;

    const startColor =
      direction === 'negative' ? PositiveMaxColor : NegativeMaxColor;
    const endColor =
      direction === 'negative' ? NegativeMaxColor : PositiveMaxColor;

    const toT = scaleLinear<number>()
      .domain([minVal, maxVal])
      .range([0, 1])
      .clamp(true);

    const interpolate = interpolateLab(startColor, endColor);

    return (value: number): string => {
      if (!Number.isFinite(value)) return NoDataFillColor;
      const t = toT(value);
      return interpolate(t) ?? NoDataFillColor;
    };
  } else {
    // Sequential
    const minVal = domain?.min ?? Math.min(0, dataMin);
    const maxVal = domain?.max ?? dataMax;

    // All values are equal
    if (minVal === maxVal) {
      return (): string =>
        direction === 'negative'
          ? (NegativeSequentialPalette[0] ?? NoDataFillColor)
          : (PositiveSequentialPalette[0] ?? NoDataFillColor);
    }

    const startColor = NeutralColor;
    const endColor =
      direction === 'negative' ? NegativeMaxColor : PositiveMaxColor;

    const toT = scaleLinear<number>()
      .domain([minVal, maxVal])
      .range([0, 1])
      .clamp(true);

    const interpolate = interpolateLab(startColor, endColor);

    return (value: number): string => {
      if (!Number.isFinite(value)) return NoDataFillColor;
      const t = toT(value);
      return interpolate(t) ?? NoDataFillColor;
    };
  }
}

export function getContrastStrokeColor(fillColor: string): string {
  if (fillColor === NoDataFillColor) {
    return NeutralPalette[6] as string;
  }

  const fillLuminance = getRelativeLuminance(fillColor);

  if (fillLuminance > 0.4) {
    // Light background - use dark stroke
    return NeutralPalette[6] as string;
  } else {
    // Dark background - use light stroke
    return NeutralPalette[3] as string;
  }
}

export type ScaleInfo = {
  range: string[];
  domain: [number, number];
};

export function getColorScaleInfo(
  values: number[],
  type: ColorScheme,
  direction: ColorSchemeDirection,
  domain?: { min?: number; max?: number; center?: number }
): { color: (value: number) => string; info: ScaleInfo } {
  if (!values.length) {
    let palette: string[];
    if (type === 'diverging') {
      if (direction === 'negative') {
        palette = [
          ...[...PositiveSequentialPalette].reverse(),
          NeutralPalette[2] as string,
          ...NegativeSequentialPalette,
        ] as string[];
      } else {
        palette = [
          ...[...NegativeSequentialPalette].reverse(),
          NeutralPalette[2] as string,
          ...PositiveSequentialPalette,
        ] as string[];
      }
    } else {
      palette = (
        direction === 'negative'
          ? [...NegativeSequentialPalette]
          : [...PositiveSequentialPalette]
      ) as string[];
    }
    return {
      color: () => NoDataFillColor,
      info: {
        range: palette,
        domain: [0, 0],
      },
    };
  }

  const [dataMin, dataMax] = extent(values) as [number, number];

  if (type === 'diverging') {
    const actualCenter = domain?.center ?? 0;
    let minVal = domain?.min ?? dataMin;
    let maxVal = domain?.max ?? dataMax;

    const maxDeviation = Math.max(
      Math.abs(minVal - actualCenter),
      Math.abs(maxVal - actualCenter)
    );
    minVal = actualCenter - maxDeviation;
    maxVal = actualCenter + maxDeviation;

    const startColor =
      direction === 'negative' ? PositiveMaxColor : NegativeMaxColor;
    const neutral = NeutralColor;
    const endColor =
      direction === 'negative' ? NegativeMaxColor : PositiveMaxColor;

    const toT = scaleLinear<number>()
      .domain([minVal, maxVal])
      .range([0, 1])
      .clamp(true);

    const interpolateLower = interpolateLab(startColor, neutral);
    const interpolateUpper = interpolateLab(neutral, endColor);

    return {
      color: (v: number) => {
        if (!Number.isFinite(v)) return NoDataFillColor;
        const t = toT(v);
        if (t <= 0.5) {
          return interpolateLower(t * 2) ?? NoDataFillColor;
        }
        return interpolateUpper((t - 0.5) * 2) ?? NoDataFillColor;
      },
      info: {
        range: [startColor, neutral, endColor],
        domain: [minVal, maxVal],
      },
    };
  }

  const minVal = domain?.min ?? Math.min(0, dataMin);
  const maxVal = domain?.max ?? dataMax;

  const palette: string[] =
    direction === 'negative'
      ? ([...NegativeSequentialPalette] as string[])
      : ([...PositiveSequentialPalette] as string[]);

  if (minVal === maxVal) {
    const singleColor =
      direction === 'negative'
        ? (NegativeSequentialPalette[0] ?? NoDataFillColor)
        : (PositiveSequentialPalette[0] ?? NoDataFillColor);
    return {
      color: () => singleColor,
      info: {
        range: palette,
        domain: [minVal, maxVal],
      },
    };
  }

  const startColor = NeutralColor;
  const endColor =
    direction === 'negative' ? NegativeMaxColor : PositiveMaxColor;

  const toT = scaleLinear<number>()
    .domain([minVal, maxVal])
    .range([0, 1])
    .clamp(true);

  const interpolate = interpolateLab(startColor, endColor);

  return {
    color: (v: number) => {
      if (!Number.isFinite(v)) return NoDataFillColor;
      const t = toT(v);
      return interpolate(t) ?? NoDataFillColor;
    },
    info: {
      range: [startColor, endColor],
      domain: [minVal, maxVal],
    },
  };
}
